import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";

import Button from "common/core/button";
import LoadingIndicator from "common/core/loading_indicator";
import { captureException } from "util/exception";
import { useFeatureFlag } from "common/feature_gating";
import { GROWTH_LANDING_PAGES_ENABLED } from "constants/feature_gates";

import styles from "./growth_landing.module.scss";

type Props = {
  hasEnvWarningBanner?: boolean;
  onContinue: () => void;
  growthLandingUrl: string;
};

export function getGrowthLandingUrl(
  orgId: string | undefined,
  documentEdges: {
    node?: { classification: { category: string | null } | null };
  }[],
) {
  const docClassification = documentEdges.find((edge) =>
    Boolean(edge.node?.classification?.category),
  );
  // @ts-expect-error global function added by a google tag manager script
  const getGrowthLandingFn = window.__getGrowthLandingUrl;
  if (
    orgId &&
    typeof getGrowthLandingFn === "function" &&
    docClassification?.node?.classification?.category
  ) {
    const docCategory = docClassification.node.classification.category.toUpperCase();
    try {
      const growthLandingUrl = getGrowthLandingFn(orgId, docCategory);
      if (growthLandingUrl) {
        // check returned value is a valid and https url
        const urlCheck = new URL(growthLandingUrl);
        if (urlCheck.protocol === "https:") {
          return growthLandingUrl;
        }
      }
    } catch (error) {
      captureException(error);
      return null;
    }
  }
  return null;
}

export function useGrowthLandingUrl(
  orgId: string | undefined,
  documentEdges: {
    node: { classification: { category: string | null } | null };
  }[],
) {
  const growthLandingPagesEnabled = useFeatureFlag(GROWTH_LANDING_PAGES_ENABLED, false);
  if (growthLandingPagesEnabled) {
    return getGrowthLandingUrl(orgId, documentEdges);
  }
  return null;
}

function GrowthLanding({ onContinue, growthLandingUrl, hasEnvWarningBanner }: Props) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  return (
    <div
      className={classNames(styles.growthContainer, hasEnvWarningBanner && styles.hasEnvWarning)}
    >
      {isLoading && <LoadingIndicator />}
      <iframe
        id="growthLanding"
        className={styles.growthIframe}
        title="landing-page-content"
        onLoad={() => {
          setIsLoading(false);
        }}
        src={growthLandingUrl}
      ></iframe>
      <div className={styles.footer}>
        <Button
          buttonSize="large"
          buttonColor="action"
          variant="secondary"
          onClick={() => navigate("/")}
        >
          <FormattedMessage
            id="1b9824e0-6eaf-4bd6-90dc-32a8c3f6d985"
            defaultMessage="Continue later"
          />
        </Button>
        <Button buttonSize="large" buttonColor="action" variant="primary" onClick={onContinue}>
          <FormattedMessage id="22044a06-f0c7-421a-9676-48abbc02cb4b" defaultMessage="Continue" />
        </Button>
      </div>
    </div>
  );
}

export default GrowthLanding;
